import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '@/hooks/useMe';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertCircle, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { Client, Fund } from '@/types';
import type { Investment } from '@/types';
import { cleanArrayField } from '@/lib/utils';
import { useDeleteClient } from '@/hooks/useClients';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface Props {
  clients: Client[];
  onEdit: (client: Client) => void;
  funds?: Fund[];
  investments?: Investment[];
  pendingTaskClientIds?: Set<string>;
}

function OriginBadge({ origin }: { origin: string }) {
  return (
    <Badge variant="outline" className="bg-transparent text-foreground border-border">
      {origin}
    </Badge>
  );
}

export function ClientsTable({ clients, onEdit, funds = [], investments = [], pendingTaskClientIds }: Props) {
  const deleteClient = useDeleteClient();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const canEditClients = isAdmin || (me?.permissions?.can_edit_clients ?? false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteClient.mutateAsync(deleteId);
      toast.success('Client removed');
    } catch {
      toast.error('Error removing client');
    }
    setDeleteId(null);
  };

  if (clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <p className="text-lg font-medium">No clients found</p>
        <p className="text-sm">Add your first client to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: "rgb(212, 163, 123)" }} className="hover:opacity-90">
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>Investor</TableHead>
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>Funds</TableHead>
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>Group</TableHead>
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>Type</TableHead>
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>POC Investor</TableHead>
              <TableHead style={{ color: "#fff", fontWeight: 600 }}>POC Jus</TableHead>
              {canEditClients && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map(client => {
              const allocatedFunds = funds
                .filter(f => investments.some(i => i.client_id === client.id && i.fund_id === f.id && (Number(i.amount_brl) > 0 || Number(i.amount_usd) > 0)))
                .map(f => f.name)
                .join(', ');
              return (
                <TableRow key={client.id} className="group">
                  <TableCell className="font-medium uppercase">
                    <button
                      onClick={() => navigate(`/client/${client.id}`)}
                      className="inline-flex items-center gap-1.5 text-left hover:text-accent hover:underline transition-colors"
                    >
                      {client.name}
                      {pendingTaskClientIds?.has(client.id) && (
                        <AlertCircle
                          className="h-3.5 w-3.5 shrink-0 text-destructive"
                          aria-label="Pending task"
                        >
                          <title>Pending task</title>
                        </AlertCircle>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">{allocatedFunds || '—'}</TableCell>
                  <TableCell><OriginBadge origin={client.origin} /></TableCell>
                  <TableCell><span className="text-sm">{client.type}</span></TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      {cleanArrayField(client.person_of_contact).map((p, i) => <div key={i} className="text-sm">{p}</div>)}
                    </div>
                  </TableCell>
                  <TableCell>{client.responsavel_jusc}</TableCell>
                  {canEditClients && (
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(client)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => setDeleteId(client.id)}>
                          <Trash2 className="mr-2 h-4 w-4" /> Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. The client will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
